import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { kayshOperator } from '@karacas/kaysh';
import { _isDev, _log } from '@shared/aux_helper_environment';
import { _ms } from '@shared/aux_helper_functions';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { _getBrandConfigLS } from 'core/services/ian-prepareBrandCustomizationV2';
import { Observable, of, ReplaySubject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { ChatMessage, ChatSessionHistoryItem } from '../../features/ai-chat/models/chat-interface';
import {
  InsightSuggestion,
  MOCK_INSIGHTS_SUGGESTIONS,
} from '../../layout/components/insights-suggestions-panel/insights-suggestions-mock.data';
import { GenieStatementResponse } from './databricks-genie-ai-chat.models';
import { DEV_TOKEN } from './dev-token';

// Interfaces para el SSE Chat Service
export interface ChatRequest {
  message: string;
  conversation_id?: string;
  agent_type?: string; // 'main', 'insight', 'pricing', 'catman', 'strategy', 'data_import'
}

// Interfaces para el proxy de Data Agent (similar a GenieProxy)
export interface DataAgentProxyRequest {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  relativePath: string;
  body?: any;
  queryParams?: { [key: string]: string };
}

// Tipos de error para Data Agent (similar a GenieErrorType)
export enum DataAgentErrorType {
  AUTH_FAILED = 'AUTH_FAILED',
  RATE_LIMIT = 'RATE_LIMIT',
  TIMEOUT = 'TIMEOUT',
  NETWORK = 'NETWORK',
  UNKNOWN = 'UNKNOWN',
  DISABLED = 'DISABLED',
  // LLM-specific error types (populated from backend error_code)
  LLM_RATE_LIMIT = 'LLM_RATE_LIMIT',
  LLM_OVERLOADED = 'LLM_OVERLOADED',
  LLM_AUTH = 'LLM_AUTH',
  LLM_NETWORK = 'LLM_NETWORK',
  LLM_TIMEOUT = 'LLM_TIMEOUT',
  LLM_SERVER_ERROR = 'LLM_SERVER_ERROR',
}

export interface DataAgentError {
  type: DataAgentErrorType;
  message: string;
  originalError?: any;
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
  relativePath?: string;
  serverResponse?: any;
}

const _keyVer = environment.localStorageKeyVerPrefix || '_v3_';

export interface SSEEvent {
  type:
    | 'start'
    | 'progress'
    | 'tool_call'
    | 'reasoning'
    | 'complete'
    | 'error'
    | 'init'
    | 'cycle_start'
    | 'text_chunk'
    | 'tool_use'
    | 'tool_start'
    | 'tool_result';
  content?: string;
  data?: any;
  error?: string;
  timestamp?: number;
  text?: string; // Para TEXT-CHUNK events
  message?: string; // Para mensajes del sistema
  cycle?: number; // Para cycle_start events
  debugInfo?: string; // Para información de debugging
  // Propiedades para tool_start
  name?: string; // Nombre de la herramienta
  input?: string; // Input de la herramienta
  tool_id?: string; // ID único del tool call
  // Propiedades para tool_result
  output?: string; // Output/resultado de la herramienta
  status?: string; // Estado del resultado: 'success' | 'error'
  // Structured error fields emitted by the Python backend
  error_code?: string; // 'rate_limit' | 'overloaded' | 'auth' | 'network' | 'timeout' | 'unknown'
  retryable?: boolean;
  metadata?: {
    tool_name?: string;
    reasoning_step?: string;
    progress_percentage?: number;
    cycle?: number;
    chunk?: string;
    isServerError?: boolean;
    serverErrorType?: string;
    troubleshooting?: string;
  };
}

export interface HealthCheckResponse {
  status: 'healthy' | 'unhealthy' | 'unknown';
  message?: string;
  timestamp: number;
  version?: string;
  uptime?: number;
  details?: any;
}

@Injectable({
  providedIn: 'root',
})
export class DataAgentSSEService {
  private eventSource: EventSource | null = null;
  private currentStreamController: AbortController | null = null; // Para cancelar fetch streams

  private readonly AGENT_PREFIX = '/api/datascience/agents/';

  // Subject para solicitar carga de sesión desde componentes externos (ej. insights)
  private _loadSessionRequest = new ReplaySubject<string>(1);

  get loadSessionRequest$(): Observable<string> {
    return this._loadSessionRequest.asObservable();
  }

  requestLoadSession(sessionId: string): void {
    _log('[DataAgentSSEService] Requesting load session:', sessionId);
    this._loadSessionRequest.next(sessionId);
  }

  constructor(private http: HttpClient, private translate: IanTranslateService) {
    if (_isDev()) {
      _log('[DataAgentSSEService] Service initialized', {
        isLocalhost: this.__isLocalHost(),
        environmentUrl: environment.apiBaseUrl_dataAgent,
        useAngularProxy: this.__needLocalhostAngularProxy(),
      });
    }
  }

  /**
   * Determina si estamos en localhost
   */
  private __isLocalHost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private __getBeProxyEndPoint(): string {
    let PROXY_ENDPOINT = `${this.AGENT_PREFIX}/proxy`;

    // Ajuste para entorno de desarrollo local
    if (this.__needLocalhostAngularProxy() === false && this.__isLocalHost()) {
      PROXY_ENDPOINT = 'https://tiendasneto.prismaretail.dev/' + PROXY_ENDPOINT;
    }

    return PROXY_ENDPOINT.replaceAll('//', '/').replace(':/', '://');
  }

  /**
   * Obtiene el token de autenticación desde localStorage o variable de entorno (dev)
   */
  private __getToken(): string {
    if (true && _isDev() && DEV_TOKEN) return DEV_TOKEN;
    let __token = localStorage.getItem(_keyVer + '__token');
    return __token ? __token.replaceAll('Bearer ', '') : null;
  }

  /**
   * Determina si hace falta usar el proxy de Angular en localhost.
   * Obtiene la configuración desde brandConfig
   */
  private __needLocalhostAngularProxy(): boolean {
    // En desarrollo, obtener del brandConfig
    if (_isDev()) {
      const brandConfig = _getBrandConfigLS();
      const dataAgentConfig = brandConfig?.dataAgent || {};
      const useProxy = dataAgentConfig.useAngularProxyInLocalhost !== false; // default true

      if (this.__isLocalHost() && useProxy) {
        return true;
      }
    }

    // En producción, nunca usar proxy local
    return false;
  }

  /**
   * Crea un payload para el proxy de Data Agent
   * @param relativePath - El endpoint relativo del Data Agent
   * @param method - Método HTTP
   * @param body - Cuerpo de la petición (opcional)
   * @param queryParams - Parámetros de query (opcional)
   */
  private __createProxyPayload(
    relativePath: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    queryParams?: { [key: string]: string }
  ): DataAgentProxyRequest {
    let proxyPayload = {
      method,
      relativePath: relativePath,
    };

    if (body) (proxyPayload as any).body = body;
    if (queryParams) (proxyPayload as any).queryParams = queryParams;

    return proxyPayload;
  }

  /**
   * Resuelve el endpoint para las peticiones al Data Agent
   * @param endpoint - El endpoint relativo (ej: 'health') o null para generar desde agentType
   * @param agentType - Tipo de agente (solo si endpoint es null)
   * @param urlPrefix - Prefijo del URL (solo si endpoint es null)
   * @param onlyRelative - Si es true, retorna solo el path relativo (para payload de proxy)
   * @returns URL completa (localhost), proxy endpoint (producción), o path relativo (onlyRelative=true)
   */
  private __resolveEndpoint(
    endpoint: string | null = null,
    agentType: string = 'main',
    urlPrefix: string = this.AGENT_PREFIX,
    onlyRelative: boolean = false
  ): string {
    // Construir endpoint si no se proporciona
    const finalEndpoint = (endpoint || `${urlPrefix}/${agentType}/stream`).replaceAll('//', '/').replace(':/', '://');

    // Si solo queremos el path relativo, retornarlo directamente
    if (onlyRelative) return finalEndpoint;

    if (this.__needLocalhostAngularProxy()) {
      // En desarrollo local, construir URL completa
      const baseUrl = environment.apiBaseUrl_dataAgent || '/dataagent';
      const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const cleanEndpoint = finalEndpoint.startsWith('/') ? finalEndpoint.slice(1) : finalEndpoint;
      const fullUrl = `${cleanBase}/${cleanEndpoint}`.replaceAll('//', '/').replace(':/', '://');

      if (_isDev()) {
        _log('[DataAgentSSEService] URL resolved (localhost):', { baseUrl, endpoint: finalEndpoint, fullUrl });
      }

      return fullUrl;
    } else {
      // En producción, retornar el endpoint del proxy
      if (_isDev()) {
        _log('[DataAgentSSEService] Using proxy (production):', {
          proxyEndpoint: this.__getBeProxyEndPoint(),
          relativePath: finalEndpoint,
        });
      }

      return this.__getBeProxyEndPoint();
    }
  }

  private __resolveEndpointStream(
    endpoint: string | null = null,
    agentType: string = 'main',
    urlPrefix: string = this.AGENT_PREFIX,
    onlyRelative: boolean = false
  ): string {
    let finalEndpoint = this.__resolveEndpoint(endpoint, agentType, urlPrefix, onlyRelative);

    if (onlyRelative || this.__needLocalhostAngularProxy()) return finalEndpoint;

    finalEndpoint = (this.__resolveEndpoint(endpoint, agentType, urlPrefix, onlyRelative) + '/stream')
      .replaceAll('//', '/')
      .replace(':/', '://');

    return finalEndpoint;
  }

  /**
   * Obtiene las headers necesarias para las peticiones
   */
  private __getAuthHeaders(): HeadersInit {
    // NO enviamos Authorization ni X-Api-Key porque el backend de Python
    // maneja la autenticación con el LLM directamente usando variables de entorno
    // El error "Could not resolve authentication method" es del SDK del LLM
    // en el backend, no del frontend

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };

    if (!this.__needLocalhostAngularProxy() && true) {
      const token = this.__getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Alternativa usando fetch con ReadableStream para manejar POST + SSE
   * Soporta tanto localhost (URL directa) como producción (via proxy)
   */
  startDataAgentStreamWithFetch(request: ChatRequest): Observable<SSEEvent> {
    return new Observable<SSEEvent>(observer => {
      const controller = new AbortController();
      this.currentStreamController = controller;

      // ── Watchdog de inactividad (hoisted al Observable para ser accesible ─────
      // desde fetchSSE, el catch y el cleanup sin problemas de scope) ─────────────
      const INACTIVITY_MS = 90_000;
      let _watchdog: ReturnType<typeof setTimeout> | null = null;
      const _clearWatchdog = () => {
        if (_watchdog) {
          clearTimeout(_watchdog);
          _watchdog = null;
        }
      };

      const fetchSSE = async () => {
        try {
          const fetchUrl = this.__resolveEndpointStream('agents/main/stream');

          let fetchBody: any;
          if (this.__needLocalhostAngularProxy()) {
            fetchBody = request;
          } else {
            fetchBody = this.__createProxyPayload('/agents/main/stream', 'POST', request);
          }

          if (_isDev()) {
            _log('[DataAgentSSEService] Starting fetch SSE:', {
              isLocal: this.__needLocalhostAngularProxy(),
              method: 'POST',
              fetchUrl: fetchUrl,
              fetchBody: fetchBody,
            });
          }

          const response = await fetch(fetchUrl, {
            method: 'POST',
            headers: this.__getAuthHeaders(),
            body: JSON.stringify(fetchBody),
            signal: controller.signal,
          });

          // ── HTTP-level error (antes de leer el body SSE) ─────────────────────
          if (!response.ok) {
            const body = await response.text().catch(() => '');
            let detail = body;
            try {
              const parsed = JSON.parse(body);
              detail = parsed?.detail || parsed?.error || parsed?.message || body;
            } catch {}
            const HTTP_STATUS_TO_CODE: Record<number, string> = {
              401: 'auth',
              429: 'rate_limit',
              503: 'overloaded',
              504: 'timeout',
            };
            const errorCode = HTTP_STATUS_TO_CODE[response.status] || 'unknown';
            observer.error({
              type: 'error' as const,
              error: detail || `HTTP ${response.status}: ${response.statusText}`,
              error_code: errorCode,
              retryable: ['rate_limit', 'overloaded'].includes(errorCode),
              timestamp: Date.now(),
              metadata: { isServerError: true, serverErrorType: errorCode },
            } as SSEEvent);
            return;
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('Response body is not readable');
          }

          // _resetWatchdog se define aquí (necesita `reader` en scope).
          // _clearWatchdog está hoisted arriba y es accesible en catch y cleanup.
          const _resetWatchdog = () => {
            _clearWatchdog();
            _watchdog = setTimeout(() => {
              controller.abort(); // causa AbortError en reader.read() → catch silencioso
              observer.error({
                type: 'error' as const,
                error: 'Sin actividad del servidor por más de 90 segundos.',
                error_code: 'timeout',
                retryable: true,
                timestamp: Date.now(),
              } as SSEEvent);
            }, INACTIVITY_MS);
          };

          _resetWatchdog();

          // Notificar inicio de conexión
          observer.next({
            type: 'start',
            content: 'Connection established',
            timestamp: Date.now(),
          });

          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              _clearWatchdog();
              observer.complete();
              break;
            }

            _resetWatchdog(); // cada chunk reinicia el watchdog

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;

              if (line.startsWith('data: ')) {
                try {
                  const eventData = line.slice(6);
                  const parsed = JSON.parse(eventData);

                  if (_isDev()) {
                    _log('[DataAgentSSEService] Received event:', parsed);
                  }

                  observer.next(parsed);

                  if (parsed.type === 'complete') {
                    _clearWatchdog();
                    observer.complete();
                    return;
                  } else if (parsed.type === 'error') {
                    _clearWatchdog();
                    observer.error(this.__enhanceErrorInfo(parsed));
                    return;
                  }
                } catch (parseError) {
                  console.error('[DataAgentSSEService] Error parsing event data:', parseError);
                  observer.next({
                    type: 'error',
                    error: `Failed to parse event: ${parseError.message}`,
                    timestamp: Date.now(),
                  });
                }
              }
            }
          }
        } catch (error) {
          _clearWatchdog(); // siempre accesible: está en el closure del Observable
          if (error instanceof Error && error.name === 'AbortError') {
            if (_isDev()) {
              _log('[DataAgentSSEService] Stream aborted by user');
            }
          } else {
            console.error('[DataAgentSSEService] Fetch SSE error:', error);
            const structuredError = this.__genericHandleError(error);
            observer.error({
              type: 'error',
              error: structuredError.message,
              timestamp: Date.now(),
              metadata: {
                httpStatus: structuredError.status,
                httpStatusText: structuredError.statusText,
                httpMethod: structuredError.method,
                relativePath: structuredError.relativePath,
                serverResponse: structuredError.serverResponse,
              },
            });
          }
        }
      };

      fetchSSE();

      return () => {
        _clearWatchdog(); // evita que el timer dispare tras unsubscribe
        controller.abort();
      };
    });
  }

  /**
   * Wrapper sobre startDataAgentStreamWithFetch que reintenta automáticamente
   * cuando el backend emite un error con retryable=true (rate_limit, overloaded, network).
   *
   * @param maxRetries  Número máximo de reintentos (default: 2)
   * @param retryBaseDelayMs  Delay base en ms; se dobla por intento (default: 2000)
   */
  startDataAgentStreamWithRetry(request: ChatRequest, maxRetries = 2, retryBaseDelayMs = 2000): Observable<SSEEvent> {
    return new Observable<SSEEvent>(observer => {
      let attempt = 0;
      let cancelled = false;
      let sub: { unsubscribe(): void } | null = null;

      const tryConnect = () => {
        sub = this.startDataAgentStreamWithFetch(request).subscribe({
          next: event => observer.next(event),
          complete: () => observer.complete(),
          error: (err: SSEEvent) => {
            const isRetryable = err?.retryable === true; // safe with old backends (undefined → false)
            if (!cancelled && isRetryable && attempt < maxRetries) {
              attempt++;
              const delay = retryBaseDelayMs * attempt;
              if (_isDev()) {
                _log(`[DataAgentSSEService] Reintentando (${attempt}/${maxRetries}) en ${delay}ms`);
              }
              observer.next({
                type: 'progress',
                content: `Reconectando con el motor de IA (intento ${attempt}/${maxRetries})...`,
                timestamp: Date.now(),
              });
              setTimeout(tryConnect, delay);
            } else {
              observer.error(err);
            }
          },
        });
      };

      tryConnect();

      return () => {
        cancelled = true;
        sub?.unsubscribe();
      };
    });
  }

  /**
   * Cierra la conexión SSE actual
   */
  closeConnection(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;

      if (_isDev()) {
        _log('[DataAgentSSEService] SSE connection closed');
      }
    }
  }

  /**
   * Cancela el stream actual (fetch-based)
   */
  cancelCurrentStream(): void {
    if (this.currentStreamController) {
      if (_isDev()) {
        _log('[DataAgentSSEService] Cancelling current stream');
      }
      this.currentStreamController.abort();
      this.currentStreamController = null;
    }
  }

  /**
   * Verifica si hay una conexión activa
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Obtiene el estado de la conexión
   */
  getConnectionState(): string {
    if (!this.eventSource) return 'CLOSED';

    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING:
        return 'CONNECTING';
      case EventSource.OPEN:
        return 'OPEN';
      case EventSource.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }

  /**
   * Método de conveniencia para testing
   */
  testConnection(): Observable<SSEEvent> {
    const testRequest: ChatRequest = {
      message: 'Test connection to data agent',
    };

    return this.startDataAgentStreamWithFetch(testRequest);
  }

  /**
   * Verifica el health de la API del Data Agent
   * @returns Promise que resuelve con el estado de salud de la API
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    try {
      if (_isDev()) {
        _log('[DataAgentSSEService] Checking health...');
      }

      if (this.__needLocalhostAngularProxy()) {
        // En localhost, llamada directa
        return await this.__checkHealthDirect();
      } else {
        // En producción, usar proxy
        return await this.__checkHealthViaProxy();
      }
    } catch (error) {
      console.error('[DataAgentSSEService] Health check failed:', error);

      let message = this.translate.instant('DATA_AGENT.ERRORS.API_UNAVAILABLE');

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          message = this.translate.instant('DATA_AGENT.ERRORS.API_TIMEOUT');
        } else if (error.message.includes('fetch')) {
          message = this.translate.instant('DATA_AGENT.ERRORS.API_CONNECTION_ERROR');
        } else {
          message = `${this.translate.instant('DATA_AGENT.ERRORS.API_ERROR_PREFIX')} ${error.message}`;
        }
      }

      return {
        status: 'unhealthy',
        message,
        timestamp: Date.now(),
      };
    }
  }

  /**
   * Verifica health mediante llamada directa (localhost)
   */
  private async __checkHealthDirect(): Promise<HealthCheckResponse> {
    const controller = new AbortController();
    let timeoutId: any;
    let isTimedOut = false;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        isTimedOut = true;
        controller.abort();
        reject(new Error(this.translate.instant('DATA_AGENT.ERRORS.API_TIMEOUT')));
      }, 15000);
    });

    try {
      // resolveEndpoint retorna URL completa para localhost
      const healthUrl = this.__resolveEndpoint('health');

      if (_isDev()) {
        _log('[DataAgentSSEService] Checking health at:', healthUrl);
      }

      const fetchPromise = fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      }).catch(err => {
        // Si el error es por abort y ya hemos marcado timeout, no lo propagamos aquí
        if (err.name === 'AbortError' && isTimedOut) {
          // El timeout promise ya rechazó, no hacer nada
          return Promise.reject(err);
        }
        throw err;
      });

      // Race entre la petición y el timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      clearTimeout(timeoutId); // Limpiar timeout inmediatamente después de recibir respuesta

      if (!response.ok) {
        const statusMsg = this.translate.instant('DATA_AGENT.ERRORS.API_STATUS_ERROR');
        return {
          status: 'unhealthy',
          message: statusMsg.replace('{{status}}', String(response.status)).replace('{{statusText}}', response.statusText),
          timestamp: Date.now(),
        };
      }

      const healthData = await response.json();

      if (_isDev()) {
        _log('[DataAgentSSEService] Health check response:', healthData);
      }

      return {
        status: 'healthy',
        message: this.translate.instant('DATA_AGENT.ERRORS.API_HEALTHY'),
        timestamp: Date.now(),
        version: healthData.version,
        uptime: healthData.uptime,
        details: healthData,
      };
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId); // Limpiar timeout en caso de error

      // Si el error es AbortError causado por timeout, lo manejamos silenciosamente
      if (error.name === 'AbortError' && isTimedOut) {
        // Ya rechazamos con el mensaje de timeout, solo propagamos
        throw new Error(this.translate.instant('DATA_AGENT.ERRORS.API_TIMEOUT'));
      }

      // Re-lanzar el error para que sea manejado por checkHealth()
      throw error;
    }
  }

  /**
   * Verifica health mediante proxy (producción)
   */
  private async __checkHealthViaProxy(): Promise<HealthCheckResponse> {
    const controller = new AbortController();
    let timeoutId: any;
    let isTimedOut = false;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        isTimedOut = true;
        controller.abort();
        reject(new Error(this.translate.instant('DATA_AGENT.ERRORS.API_TIMEOUT')));
      }, 15000);
    });

    try {
      // resolveEndpoint retorna URL completa para localhost
      const proxyUrl = this.__getBeProxyEndPoint();

      if (_isDev()) {
        _log('[DataAgentSSEService] Checking health at:', proxyUrl);
      }

      const fetchPromise = fetch(proxyUrl, {
        method: 'POST',
        headers: {
          ...this.__getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(this.__createProxyPayload('/health', 'GET')),
        signal: controller.signal,
      }).catch(err => {
        // Si el error es por abort y ya hemos marcado timeout, no lo propagamos aquí
        if (err.name === 'AbortError' && isTimedOut) {
          // El timeout promise ya rechazó, no hacer nada
          return Promise.reject(err);
        }
        throw err;
      });

      // Race entre la petición y el timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      clearTimeout(timeoutId); // Limpiar timeout inmediatamente después de recibir respuesta

      if (!response.ok) {
        const statusMsg = this.translate.instant('DATA_AGENT.ERRORS.API_STATUS_ERROR');
        return {
          status: 'unhealthy',
          message: statusMsg.replace('{{status}}', String(response.status)).replace('{{statusText}}', response.statusText),
          timestamp: Date.now(),
        };
      }

      const healthData = await response.json();

      if (_isDev()) {
        _log('[DataAgentSSEService] Health check response:', healthData);
      }

      return {
        status: 'healthy',
        message: this.translate.instant('DATA_AGENT.ERRORS.API_HEALTHY'),
        timestamp: Date.now(),
        version: healthData.version,
        uptime: healthData.uptime,
        details: healthData,
      };
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId); // Limpiar timeout en caso de error

      // Si el error es AbortError causado por timeout, lo manejamos silenciosamente
      if (error.name === 'AbortError' && isTimedOut) {
        // Ya rechazamos con el mensaje de timeout, solo propagamos
        throw new Error(this.translate.instant('DATA_AGENT.ERRORS.API_TIMEOUT'));
      }

      // Re-lanzar el error para que sea manejado por checkHealth()
      throw error;
    }
  }

  /**
   * Helper unificado para realizar llamadas HTTP al Data Agent
   * Maneja la lógica de Proxy vs Localhost automáticamente
   * Usa fetch API para evitar interceptores de Angular si es necesario
   */
  private __callDataAgent<T>(
    relativePath: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    queryParams?: { [key: string]: string }
  ): Observable<T> {
    return new Observable<T>(observer => {
      const controller = new AbortController();

      const performFetch = async () => {
        try {
          const headers = this.__getAuthHeaders();
          let fetchUrl: string;
          let fetchMethod: string;
          let fetchBody: string | undefined;

          if (this.__needLocalhostAngularProxy()) {
            // ============================================
            // LOCALHOST: Llamada directa
            // ============================================
            fetchUrl = this.__resolveEndpoint(relativePath);
            fetchMethod = method;

            // Manejo de Query Params para URL
            if (queryParams && Object.keys(queryParams).length > 0) {
              const params = new URLSearchParams();
              Object.keys(queryParams).forEach(key => {
                params.append(key, queryParams[key]!);
              });
              const queryString = params.toString();
              fetchUrl = fetchUrl + (queryString ? '?' + queryString : '');
            }

            // Manejo de Body
            if (body && method !== 'GET') {
              fetchBody = JSON.stringify(body);
            }
          } else {
            // ============================================
            // PRODUCCIÓN: Usar Proxy
            // ============================================
            fetchUrl = this.__getBeProxyEndPoint();
            fetchMethod = 'POST'; // El proxy siempre recibe POST

            // Crear payload para el proxy
            const proxyPayload = this.__createProxyPayload(relativePath, method, body, queryParams);
            fetchBody = JSON.stringify(proxyPayload);
          }

          if (_isDev()) {
            _log(`[DataAgentSSEService] Fetching ${method} ${relativePath}`, {
              fetchUrl,
              fetchMethod,
              bodyLength: fetchBody?.length,
            });
          }

          const response = await fetch(fetchUrl, {
            method: fetchMethod,
            headers: headers,
            body: fetchBody,
            signal: controller.signal,
          });

          if (!response.ok) {
            // Crear un HttpErrorResponse similar para mantener consistencia
            const errorBody = await response.text().catch(() => '');
            let errorObj: any;
            try {
              errorObj = JSON.parse(errorBody);
            } catch {
              errorObj = errorBody;
            }

            const httpError = new HttpErrorResponse({
              error: errorObj,
              headers: response.headers as any,
              status: response.status,
              statusText: response.statusText,
              url: fetchUrl,
            });

            // Crear request payload para el error handler
            const requestPayload: DataAgentProxyRequest = {
              method,
              relativePath,
              body,
              queryParams,
            };

            throw this.__genericHandleError(httpError, requestPayload);
          }

          const data = await response.json();

          // Unwrapping de respuesta del Proxy si es necesario
          let result = data;
          if (!this.__needLocalhostAngularProxy()) {
            // Si el proxy devuelve { content: ... }, extraemos content.
            // Si no, devolvemos data tal cual (comportamiento robusto)
            result = data && data.content !== undefined ? data.content : data;
          }

          observer.next(result as T);
          observer.complete();
        } catch (error: any) {
          if (error.name === 'AbortError') {
            // Request cancelado
          } else {
            console.error('[DataAgentSSEService] Fetch error:', error);

            // Si el error ya es un DataAgentError estructurado, usarlo directamente
            // Si no, procesarlo con __genericHandleError
            const structuredError = error.type && error.message ? error : this.__genericHandleError(error);

            observer.error(structuredError);
          }
        }
      };

      performFetch();

      return () => {
        controller.abort();
      };
    });
  }

  /**
   * Mejora la información del error para debugging.
   * Prioriza el campo error_code estructurado del backend; cae a heurísticas de string como fallback.
   */
  private __enhanceErrorInfo(errorEvent: SSEEvent): SSEEvent {
    if (!errorEvent.error) return errorEvent;

    let debugInfo = '';
    let serverErrorType = errorEvent.metadata?.serverErrorType || 'unknown';
    let troubleshooting = '';

    // ── Camino 1: error_code estructurado emitido por el backend Python ──────
    if (errorEvent.error_code) {
      switch (errorEvent.error_code) {
        case 'rate_limit':
          serverErrorType = DataAgentErrorType.LLM_RATE_LIMIT;
          debugInfo = '[RATE LIMIT] ';
          troubleshooting =
            'El motor de IA (Prisma LLM) superó el límite de solicitudes. El sistema reintentó automáticamente. Intenta de nuevo en unos segundos.';
          break;
        case 'overloaded':
          serverErrorType = DataAgentErrorType.LLM_OVERLOADED;
          debugInfo = '[LLM OVERLOADED] ';
          troubleshooting = 'El motor de IA está temporalmente sobrecargado. Intenta de nuevo en unos momentos.';
          break;
        case 'auth':
          serverErrorType = DataAgentErrorType.LLM_AUTH;
          debugInfo = '[AUTH ERROR] ';
          troubleshooting = 'Error de autenticación con el LLM. Verificar la ANTHROPIC_API_KEY en el servidor.';
          break;
        case 'network':
          serverErrorType = DataAgentErrorType.LLM_NETWORK;
          debugInfo = '[NETWORK ERROR] ';
          troubleshooting = 'Error de conexión con el motor de IA. Verificar conectividad del servidor.';
          break;
        case 'timeout':
          serverErrorType = DataAgentErrorType.LLM_TIMEOUT;
          debugInfo = '[TIMEOUT] ';
          troubleshooting = 'Timeout en la conexión con el LLM. El servidor tardó demasiado en responder.';
          break;
        case 'llm_server_error':
          serverErrorType = DataAgentErrorType.LLM_SERVER_ERROR;
          debugInfo = '[LLM SERVER ERROR] ';
          troubleshooting = 'Error interno del servidor de IA. Intenta de nuevo.';
          break;
        default:
          serverErrorType = 'unknown';
          debugInfo = `[ERROR ${errorEvent.error_code}] `;
      }
      debugInfo += `error_code=${errorEvent.error_code}`;
      if (errorEvent.retryable !== undefined) {
        debugInfo += ` retryable=${errorEvent.retryable}`;
      }
    }
    // ── Camino 2: heurísticas legacy sobre el string del error ───────────────
    else if (errorEvent.error.includes('tool_use') && errorEvent.error.includes('tool_result')) {
      serverErrorType = 'prisma_llm_api_error';
      debugInfo = '[PRISMA LLM API ERROR] ';
      const toolIdMatch = errorEvent.error.match(/toolu_[a-zA-Z0-9]+/);
      if (toolIdMatch) debugInfo += `Tool ID: ${toolIdMatch[0]} `;
      const messageMatch = errorEvent.error.match(/messages\.(\d+):/);
      if (messageMatch) debugInfo += `En mensaje #${messageMatch[1]} `;
      debugInfo += '— Error del SERVIDOR PYTHON.';
      troubleshooting = 'Revisar el código Python: cada tool_use debe tener su tool_result correspondiente.';
    }

    return {
      ...errorEvent,
      debugInfo,
      metadata: {
        ...errorEvent.metadata,
        isServerError: true,
        serverErrorType,
        troubleshooting: troubleshooting || errorEvent.metadata?.troubleshooting,
      },
    };
  }

  /**
   * Maneja errores HTTP y los transforma en errores estructurados
   * Similar al manejo de errores en databricks-genie-ai-chat.service.ts
   */
  private __genericHandleError(error: HttpErrorResponse | any, request?: DataAgentProxyRequest): DataAgentError {
    let dataAgentError: DataAgentError = {
      type: DataAgentErrorType.UNKNOWN,
      message: 'An unknown error occurred',
      originalError: error,
    };

    // Si no es un HttpErrorResponse, crear uno básico
    if (!(error instanceof HttpErrorResponse)) {
      dataAgentError.message = error?.message || 'Unknown error';
      return dataAgentError;
    }

    // Intentar extraer mensaje del backend
    let serverMessage = '';
    if (error.error?.errorMessage) {
      serverMessage = error.error.errorMessage;
    } else if (error.error?.message) {
      serverMessage = error.error.message;
    } else if (error.error?.error) {
      serverMessage = error.error.error;
    } else if (typeof error.error === 'string') {
      serverMessage = error.error;
    }

    // Status 0 indica problemas de red, CORS, o servidor no disponible
    if (error.status === 0) {
      dataAgentError = {
        type: DataAgentErrorType.NETWORK,
        message: this.translate.instant('DATA_AGENT.ERRORS.NETWORK_ERROR'),
        originalError: error,
      };
    } else if (error.status === 401) {
      dataAgentError = {
        type: DataAgentErrorType.AUTH_FAILED,
        message: serverMessage || 'Authentication failed',
        originalError: error,
      };
    } else if (error.status === 429) {
      dataAgentError = {
        type: DataAgentErrorType.RATE_LIMIT,
        message: serverMessage || 'Rate limit exceeded',
        originalError: error,
      };
    } else if (error.status === 500) {
      dataAgentError = {
        type: DataAgentErrorType.UNKNOWN,
        message: serverMessage || error.message || 'Internal Server Error',
        originalError: error,
      };
    } else if (error.status === 502) {
      dataAgentError = {
        type: DataAgentErrorType.NETWORK,
        message: serverMessage || this.translate.instant('DATA_AGENT.ERRORS.BAD_GATEWAY'),
        originalError: error,
      };
    } else if (error.status === 503) {
      dataAgentError = {
        type: DataAgentErrorType.NETWORK,
        message: serverMessage || this.translate.instant('DATA_AGENT.ERRORS.SERVICE_UNAVAILABLE'),
        originalError: error,
      };
    } else if (error.status === 504) {
      dataAgentError = {
        type: DataAgentErrorType.TIMEOUT,
        message: serverMessage || this.translate.instant('DATA_AGENT.ERRORS.GATEWAY_TIMEOUT'),
        originalError: error,
      };
    } else {
      dataAgentError = {
        type: DataAgentErrorType.UNKNOWN,
        message: serverMessage || error.message || `HTTP Error ${error.status}`,
        originalError: error,
      };
    }

    // Preservar información HTTP adicional
    dataAgentError.status = error.status;
    dataAgentError.statusText = error.statusText;
    dataAgentError.url = error.url || undefined;

    // Preservar información del request (método y path)
    if (request) {
      dataAgentError.method = request.method;
      dataAgentError.relativePath = request.relativePath;
    }

    // Preservar el response completo del servidor para debugging
    if (error.error) {
      dataAgentError.serverResponse = error.error;
    }

    return dataAgentError;
  }

  /**
   * ============================================
   * MÉTODOS DE HISTORIAL DE SESIONES
   * ============================================
   */

  /**
   * Obtiene el historial de sesiones.
   * TODO: Conectar con API backend real cuando esté disponible (actualmente usa mock data)
   * Endpoint esperado: GET /api/chat/sessions?agentType={type}
   */
  getSessionHistory(agentType: string): Observable<ChatSessionHistoryItem[]> {
    if (false) {
      // Mock data temporal
      const now = new Date();
      const mockData: ChatSessionHistoryItem[] = [
        {
          id: 'session-001',
          name: 'Análisis de ventas Q4 2024',
          description: 'Revisión completa de métricas de ventas del último trimestre',
          date: new Date(now.getTime() - 2 * 60 * 60 * 1000), // Hace 2 horas
          messageCount: 12,
          agentType: agentType,
          lastMessage: '¿Cuáles son las categorías que más vendieron?',
        },
      ];

      // Simular delay de red
      return of(mockData).pipe(delay(500));
    }

    const queryParams = { agent_type: agentType };
    return this.__callDataAgent<any>('/agents/history', 'GET', undefined, queryParams).pipe(
      kayshOperator('__callDataAgent', null, { maxTime: _ms('1m') }),
      map(response => {
        // En producción (via proxy), 'response' puede venir como un string JSON dentro de 'content'
        // o ya parseado si __callDataAgent hizo el trabajo, pero el log muestra que 'content' es un string JSON.
        // Además, la estructura real parece ser { success: boolean, history: [...] } dentro de ese contenido.

        let data = response;

        // Si es string, intentar parsear
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error('[DataAgentSSEService] Error parsing history data string:', e);
            return [];
          }
        }

        // Si tenemos la estructura { history: [...] }
        if (data && Array.isArray(data.history)) {
          return data.history;
        }

        // Si data es directamente el array (caso legacy o diferente endpoint)
        if (Array.isArray(data)) {
          return data;
        }

        _log('[DataAgentSSEService] Unexpected history format:', data);
        return [];
      }),
      map((items: any[]) => this.__mapSessionHistoryItems(items, agentType))
    );
  }

  /**
   * Mapea los items de historial crudos a la interfaz ChatSessionHistoryItem
   */
  private __mapSessionHistoryItems(items: any[], agentType: string): ChatSessionHistoryItem[] {
    return items.map(item => ({
      // id es el ID numérico de la fila en BD (para pedir mensajes vía /agents/history/{id})
      id: String(item.session_id || item.id),
      // conversationId es el string largo tipo "streaming-conv-..." que el backend usa para memoria
      conversationId: item.conversation_id || undefined,
      name: item.name || item.title || item.query || 'Session ' + (item.conversation_id || item.id),
      description: item.response ? item.response.substring(0, 150) + '...' : item.description || '',
      date: new Date(item.created_at || item.date || Date.now()),
      messageCount:
        item.message_count ||
        (item.reasoning_traces
          ? (() => {
              try {
                const traces = typeof item.reasoning_traces === 'string' ? JSON.parse(item.reasoning_traces) : item.reasoning_traces;
                return Array.isArray(traces) ? traces.length + 2 : 2;
              } catch {
                return 2;
              }
            })()
          : 2),
      agentType: item.agent_type || agentType,
      lastMessage: item.last_message || item.query || '',
    }));
  }

  /**
   * Obtiene los mensajes de una sesión específica.
   * Devuelve los mensajes Y el conversationId real (string largo) extraído de la respuesta,
   * para que el componente pueda mantener el contexto correcto en el backend.
   */
  getSessionMessages(sessionId: string): Observable<{ messages: ChatMessage[]; conversationId?: string }> {
    // El endpoint es /agents/history/{id} donde id es el ID numérico del insight historial
    const endpoint = `/agents/history/${sessionId}`;

    return this.__callDataAgent<any>(endpoint, 'GET').pipe(
      kayshOperator('getSessionMessages', sessionId, { maxTime: _ms('1m') }),
      map(response => {
        let data = response;

        // Parseo necesario si viene como string
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error('[DataAgentSSEService] Error parsing session messages string:', e);
            return { messages: [], conversationId: undefined };
          }
        }

        // Estructura esperada: data puede ser el objeto history item directamente
        // { id: 105, conversation_id: '...', query: '...', response: '...', ... }

        let item = data;
        // Si viene envuelto
        if (data && data.history_item) item = data.history_item;
        if (data && data.content) {
          // Si viene en content (proxy) y no fue parseado antes, intentar parsear
          try {
            const contentParsed = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;

            // Caso donde viene envuelto en "insight" (ver response user)
            if (contentParsed.insight) {
              item = contentParsed.insight;
            } else {
              item = contentParsed;
            }
          } catch (e) {
            /* ignore */
          }
        } else if (data && data.insight) {
          // Caso donde no viene via proxy o ya fue parseado antes pero tiene la propiedad insight
          item = data.insight;
        }

        // Como la API de history devuelve items individuales (parejas query/response),
        // pero getSessionMessages espera una lista de mensajes, convertimos el par Q&A
        // en dos mensajes separados.
        // Si el endpoint devolviera una lista (conversación completa), iteraríamos sobre ella.

        const messages: ChatMessage[] = [];

        if (item) {
          // 1. Mensaje del usuario (query)
          if (item.query) {
            messages.push({
              from: 'user',
              text: item.query,
              isStreaming: false,
              textComplete: true,
            });
          }

          // 2. Respuesta del agente (response)
          if (item.response) {
            const aiMessage: ChatMessage = {
              from: 'ai',
              text: item.response,
              isStreaming: false,
              textComplete: true,
            };

            // Intentar parsear reasoning traces si existen
            if (item.reasoning_traces) {
              try {
                const traces = typeof item.reasoning_traces === 'string' ? JSON.parse(item.reasoning_traces) : item.reasoning_traces;

                if (Array.isArray(traces) && traces.length > 0) {
                  aiMessage.cycleHistory = traces.map((trace: any, index: number) => {
                    // Extraer número de ciclo
                    let cycleNum = index + 1;
                    if (trace.cycle && typeof trace.cycle === 'string') {
                      const match = trace.cycle.match(/\d+/);
                      if (match) cycleNum = parseInt(match[0], 10);
                    } else if (typeof trace.cycle === 'number') {
                      cycleNum = trace.cycle;
                    }

                    // Contenido: reasoning_text puede ser array
                    let content = '';
                    if (Array.isArray(trace.reasoning_text)) {
                      content = trace.reasoning_text.join('\n\n');
                    } else {
                      content = trace.reasoning_text || '';
                    }

                    return {
                      cycle: cycleNum,
                      title: trace.cycle || `Ciclo ${cycleNum}`,
                      content: content,
                      toolCalls: trace.tool_calls || [],
                    };
                  });
                }
              } catch (e) {
                console.error('[DataAgentSSEService] Error parsing reasoning traces:', e);
              }
            }

            messages.push(aiMessage);
          }
        }

        // Extraer el conversationId real de la respuesta para devolvérselo al componente
        const resolvedConversationId: string | undefined = item?.conversation_id || undefined;

        if (_isDev()) {
          _log('[DataAgentSSEService] getSessionMessages resolved conversationId:', resolvedConversationId, 'from sessionId:', sessionId);
        }

        return { messages, conversationId: resolvedConversationId };
      })
    );
  }

  /**
   * Obtiene las sugerencias de insights disponibles.
   * TODO: Conectar con API backend real cuando esté disponible (actualmente usa mock data)
   * Endpoint esperado: GET /api/insights/suggestions
   */
  getInsightsSuggestions(useMock: boolean = false): Observable<InsightSuggestion[]> {
    if (useMock) {
      // Mock data temporal
      return of([...MOCK_INSIGHTS_SUGGESTIONS]).pipe(delay(500));
    }

    const endpoint = '/agents/main/suggestions';

    return this.__callDataAgent<any>(endpoint, 'GET').pipe(
      kayshOperator('getInsightsSuggestions', null, { maxTime: _ms('2m') }),
      map(response => {
        let data = response;

        // Parseo si viene como string
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error('[DataAgentSSEService] Error parsing suggestions string:', e);
            return [];
          }
        }

        // Caso: Respuesta anidada en data.items (formato paginado) directamente
        if (data && data.data && Array.isArray(data.data.items)) {
          return data.data.items;
        }

        // Manejo de respuesta proxy (content) - por si acaso
        if (data && data.content) {
          try {
            const contentParsed = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;

            // Caso: Respuesta anidada en data.items dentro de content
            if (contentParsed.data && Array.isArray(contentParsed.data.items)) {
              return contentParsed.data.items;
            }

            // Si la respuesta tiene estructura { success: true, suggestions: [...] }
            if (contentParsed.suggestions) {
              return contentParsed.suggestions;
            }
            // Si devuelve array directo
            if (Array.isArray(contentParsed)) {
              return contentParsed;
            }
            return contentParsed;
          } catch (e) {
            // Si falla el parseo, intentar devolver data.content tal cual si es array
            if (Array.isArray(data.content)) return data.content;
          }
        }

        // Si la respuesta directa tiene propiedad suggestions
        if (data && data.suggestions) return data.suggestions;

        // Si es array directo
        if (Array.isArray(data)) return data;

        _log('[DataAgentSSEService] Unexpected suggestions format:', data);
        return [];
      }),
      // Validación y transformación opcional si la API devuelve estructura diferente a InsightSuggestion
      map((items: any[]) =>
        items.map(
          item =>
            ({
              id: item.id || '',
              title: item.description || item.title || this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.DEFAULT_TITLE_SUGGESTION'),
              description: item.reason || item.description || '',
              type: item.suggestion_type === 'price_change' ? 'precios' : item.type || 'insight', // Mapeo de tipos
              status: item.suggestion_status === 'pending_approval' ? 'pending' : item.status || 'pending',
              priority: item.priority || 'media',
              date: new Date(item.created_at || Date.now()),
              tags: item.tags || [],
              detailedReport: item.reason, // Usar reason como reporte detallado por ahora
              suggestions_json: item.suggestions_json, // Mantener raw data si es necesario
              // Mapear otros campos si es necesario
              explanation_summary: item.explanation_summary,
              action_type: item.action_type,
              sql_query: item.sql_query,
              generated_at: item.created_at,
              estimatedImpact: item.estimated_impact || Math.floor(Math.random() * 90000) + 10000,
              insight_id: item.conversation_id || item.insight_id || item.id,
              tableData: this.__transformSuggestionsJsonToTableData(item.suggestions_json, item.id || ''),
            } as InsightSuggestion)
        )
      )
    );
  }

  private __transformSuggestionsJsonToTableData(suggestionJson: string | any[], suggestionId: string): GenieStatementResponse | undefined {
    if (!suggestionJson) return undefined;
    try {
      let parsedItems = suggestionJson;
      if (typeof suggestionJson === 'string') {
        parsedItems = JSON.parse(suggestionJson);
      }

      if (Array.isArray(parsedItems) && parsedItems.length > 0) {
        const tableData: GenieStatementResponse = {
          statement_response: {
            statement_id: 'generated-from-json-' + suggestionId,
            status: { state: 'SUCCEEDED' },
            manifest: {
              format: 'JSON_ARRAY',
              schema: {
                column_count: 4,
                columns: [
                  {
                    name: this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SCHEMA.ITEM'),
                    type_text: 'INT',
                    type_name: 'INT',
                    position: 0,
                  },
                  {
                    name: this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SCHEMA.STORE'),
                    type_text: 'INT',
                    type_name: 'INT',
                    position: 1,
                  },
                  {
                    name: this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SCHEMA.CURRENT_PRICE'),
                    type_text: 'DECIMAL',
                    type_name: 'DECIMAL',
                    position: 2,
                  },
                  {
                    name: this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SCHEMA.NEW_PRICE'),
                    type_text: 'DECIMAL',
                    type_name: 'DECIMAL',
                    position: 3,
                  },
                ],
              },
              total_chunk_count: 1,
              chunks: [{ chunk_index: 0, row_offset: 0, row_count: parsedItems.length, byte_count: 0 }],
              total_row_count: parsedItems.length,
              total_byte_count: 0,
              truncated: false,
            },
            result: {
              chunk_index: 0,
              row_offset: 0,
              row_count: parsedItems.length,
              data_typed_array: parsedItems.map(item => ({
                values: [
                  { str: String(item.item_id || '') },
                  { str: String(item.store_id || '') },
                  { num: Number(item.current_price || 0) },
                  { num: Number(item.new_price || 0) },
                ],
              })),
            },
          },
        };
        return tableData;
      }
    } catch (e) {
      console.error('[DataAgentSSEService] Error parsing suggestionJson in transform:', e);
    }
    return undefined;
  }

  /**
   * Cleanup al destruir el servicio
   */
  ngOnDestroy(): void {
    this.closeConnection();
  }
}
